import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { UserPlus, Pencil, Trash2, Loader2, Mail, Phone, Users, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { ReassignLesseeDialog, UnassignedLesseesCard } from '../components/leasing/ReassignLessee';
import { AssignmentLogCard } from '../components/leasing/AssignmentLogCard';

const EMPTY = { name: '', email: '', password: '', phone: '', client_ids: [] };

const ClientPicker = ({ clients, selected, onChange, el }) => (
    <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500" data-testid="manager-client-count">{el ? `Επιλεγμένοι: ${selected.length} από ${clients.length}` : `Selected: ${selected.length} of ${clients.length}`}</span>
            <button type="button" onClick={() => onChange(selected.length === clients.length ? [] : clients.map((c) => c.id))} className="text-[#1B7EB5] hover:underline" data-testid="manager-client-toggle-all">
                {selected.length === clients.length && clients.length ? (el ? 'Καμία επιλογή' : 'Clear all') : (el ? 'Επιλογή όλων' : 'Select all')}
            </button>
        </div>
    <div className="space-y-2 max-h-56 overflow-y-auto border border-slate-200 rounded-sm p-3" data-testid="manager-client-picker">
        {clients.length === 0 && <p className="text-sm text-slate-400">{el ? 'Δεν υπάρχουν μισθωτές' : 'No lessees'}</p>}
        {clients.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <Checkbox
                    checked={selected.includes(c.id)}
                    onCheckedChange={(on) => onChange(on ? [...selected, c.id] : selected.filter((id) => id !== c.id))}
                    data-testid={`manager-client-${c.id}`}
                />
                {c.name}
            </label>
        ))}
    </div>
    </div>
);

const ManagerDialog = ({ open, onOpenChange, manager, clients, el, onSaved }) => {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const isEdit = Boolean(manager);

    useEffect(() => {
        if (open) setForm(manager ? { ...EMPTY, name: manager.name, phone: manager.phone || '', client_ids: manager.assigned_client_ids || [] } : EMPTY);
    }, [open, manager]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const save = async () => {
        setSaving(true);
        try {
            if (isEdit) {
                await api.put(`/leasing/managers/${manager.id}`, { name: form.name, phone: form.phone, client_ids: form.client_ids });
            } else {
                await api.post('/leasing/managers', form);
            }
            toast.success(el ? 'Αποθηκεύτηκε' : 'Saved');
            onOpenChange(false);
            onSaved();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white max-w-lg" data-testid="manager-dialog">
                <DialogHeader>
                    <DialogTitle>{isEdit ? (el ? 'Επεξεργασία υπευθύνου' : 'Edit fleet manager') : (el ? 'Νέος υπεύθυνος στόλου' : 'New fleet manager')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1"><Label>{el ? 'Ονοματεπώνυμο' : 'Full name'}</Label><Input value={form.name} onChange={set('name')} data-testid="manager-name" /></div>
                        <div className="space-y-1"><Label>{el ? 'Τηλέφωνο' : 'Phone'}</Label><Input value={form.phone} onChange={set('phone')} data-testid="manager-phone" /></div>
                    </div>
                    {!isEdit && (
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.email} onChange={set('email')} data-testid="manager-email" /></div>
                            <div className="space-y-1"><Label>{el ? 'Κωδικός' : 'Password'}</Label><Input type="password" value={form.password} onChange={set('password')} data-testid="manager-password" /></div>
                        </div>
                    )}
                    <div className="space-y-1">
                        <Label>{el ? 'Μισθωτές που βλέπει' : 'Lessees they can see'}</Label>
                        <ClientPicker clients={clients} selected={form.client_ids} onChange={(ids) => setForm({ ...form, client_ids: ids })} el={el} />
                    </div>
                    <Button onClick={save} disabled={saving} className="w-full bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm" data-testid="manager-save">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (el ? 'Αποθήκευση' : 'Save')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const ManagerStats = ({ stats, id, el }) => {
    const tiles = [
        { k: 'lessees', label: el ? 'Μισθωτές' : 'Lessees', cls: 'text-[#1B7EB5]' },
        { k: 'vehicles', label: el ? 'Οχήματα' : 'Vehicles', cls: 'text-[#1B7EB5]' },
        { k: 'open_alerts', label: el ? 'Ανοιχτές ειδοποιήσεις' : 'Open alerts', cls: stats.open_alerts ? 'text-red-600' : 'text-slate-400' },
        { k: 'open_requests', label: el ? 'Νέα αιτήματα' : 'New requests', cls: stats.open_requests ? 'text-purple-600' : 'text-slate-400' },
    ];
    return (
        <div className="flex gap-5 text-center" data-testid={`manager-stats-${id}`}>
            {tiles.map((t) => (
                <div key={t.k}><p className={`text-xl font-bold ${t.cls}`} data-testid={`manager-stat-${t.k}-${id}`}>{stats[t.k]}</p><p className="text-[11px] text-slate-500 max-w-[80px] leading-tight">{t.label}</p></div>
            ))}
        </div>
    );
};

const ManagerCard = ({ m, clientNames, el, onEdit, onDelete, onMove }) => (
    <div className="fleet-card p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`manager-card-${m.id}`}>
        <div className="flex-1">
            <p className="font-semibold text-slate-800">{m.name}</p>
            <div className="flex flex-wrap gap-4 mt-1 text-sm text-slate-500">
                <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{m.email}</span>
                <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{m.phone || '-'}</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-3" data-testid={`manager-clients-${m.id}`}>
                {m.assigned_client_ids.length === 0 && <span className="text-xs text-amber-600">{el ? 'Δεν έχουν ανατεθεί μισθωτές' : 'No lessees assigned'}</span>}
                {m.assigned_client_ids.map((id) => (
                    <span key={id} className="inline-flex items-center gap-1 text-xs bg-[#1B7EB5]/10 text-[#1B7EB5] pl-2 pr-1 py-0.5 rounded" data-testid={`manager-chip-${m.id}-${id}`}>
                        {clientNames[id] || id}
                        <button onClick={() => onMove({ id, name: clientNames[id] || id })} className="p-0.5 rounded hover:bg-[#1B7EB5]/20" title={el ? 'Μεταφορά σε άλλον υπεύθυνο' : 'Move to another manager'} data-testid={`manager-chip-move-${m.id}-${id}`}>
                            <ArrowRightLeft className="w-3 h-3" />
                        </button>
                    </span>
                ))}
            </div>
        </div>
        {m.stats && <ManagerStats stats={m.stats} id={m.id} el={el} />}
        <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={onEdit} className="text-[#1B7EB5]" data-testid={`manager-edit-${m.id}`}><Pencil className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={onDelete} className="text-red-500" data-testid={`manager-delete-${m.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
    </div>
);

export default function LeasingManagersPage() {
    const { language } = useLanguage();
    const el = language === 'el';
    const [managers, setManagers] = useState(null);
    const [clients, setClients] = useState([]);
    const [dialog, setDialog] = useState({ open: false, manager: null });
    const [move, setMove] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const reload = () => { load(); setRefreshKey((k) => k + 1); };

    const load = useCallback(async () => {
        const [m, c] = await Promise.all([api.get('/leasing/managers'), api.get('/leasing/clients')]);
        setManagers(m.data);
        setClients(c.data.map(({ id, name }) => ({ id, name })));
    }, []);

    useEffect(() => { load().catch(() => setManagers([])); }, [load]);

    const remove = async (m) => {
        if (!window.confirm(el ? `Διαγραφή του ${m.name};` : `Delete ${m.name}?`)) return;
        try {
            await api.delete(`/leasing/managers/${m.id}`);
            reload();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        }
    };

    const clientNames = Object.fromEntries(clients.map((c) => [c.id, c.name]));

    return (
        <div className="space-y-6" data-testid="leasing-managers-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">{el ? 'Υπεύθυνοι Στόλου' : 'Fleet Managers'}</h1>
                    <p className="text-zinc-500">{el ? 'Κάθε υπεύθυνος βλέπει μόνο τους μισθωτές που του αναθέτετε' : 'Each manager only sees the lessees you assign'}</p>
                </div>
                <Button onClick={() => setDialog({ open: true, manager: null })} className="bg-[#1B7EB5] hover:bg-[#58A6B4] text-white rounded-sm" data-testid="add-manager-btn">
                    <UserPlus className="w-4 h-4 mr-2" />{el ? 'Νέος υπεύθυνος' : 'New manager'}
                </Button>
            </div>
            <UnassignedLesseesCard el={el} refreshKey={refreshKey} onChanged={() => { load(); setRefreshKey((k) => k + 1); }} />
            {managers === null ? <Loader2 className="w-6 h-6 animate-spin text-[#1B7EB5]" /> : managers.length ? (
                managers.map((m) => (
                    <ManagerCard key={m.id} m={m} clientNames={clientNames} el={el} onEdit={() => setDialog({ open: true, manager: m })} onDelete={() => remove(m)} onMove={(client) => setMove({ client, from: m })} />
                ))
            ) : (
                <div className="fleet-card p-10 text-center text-slate-500" data-testid="managers-empty">
                    <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    {el ? 'Δεν υπάρχουν υπεύθυνοι στόλου ακόμα.' : 'No fleet managers yet.'}
                </div>
            )}
            <AssignmentLogCard el={el} refreshKey={refreshKey} />
            <ReassignLesseeDialog open={Boolean(move)} onOpenChange={(o) => !o && setMove(null)} client={move?.client} fromManager={move?.from} el={el} onDone={reload} />
            <ManagerDialog open={dialog.open} onOpenChange={(open) => setDialog({ ...dialog, open })} manager={dialog.manager} clients={clients} el={el} onSaved={reload} />
        </div>
    );
}
